// =============================================================================
// src/lib/chwFieldCore.js - pure logic for CHW field mode (no React, no
// network). Unit tested in src/lib/__tests__/chwField.test.mjs.
//
//   Visit state       fieldVisitState(), minutesLabel(), expectedEnd()
//   Errors            fieldErrorMessage(), isNetworkError()
//   Engagement drafts draftKey(), pickDraftFields(), saveDraft(), loadDraft(),
//                     clearDraft(), clearAllFieldDrafts(), draftAgeLabel()
//   Unread counts     unreadFor(), unreadTotal()
//
// Drafts are "minimum necessary": only the form fields of the Log engagement
// form, keyed by user + visit (or member), in localStorage under one prefix so
// sign-out can wipe them all. They expire after DRAFT_TTL_MS and are never
// submitted without the CHW pressing Save.
// =============================================================================

export const DRAFT_PREFIX = "pos.chwDraft.v1.";
// Drafts hold a member's note on a shared-risk device: short life, swept on
// every app start (sweepExpiredDrafts) and wiped on sign-out.
export const DRAFT_TTL_MS = 12 * 3600 * 1000;

// The only fields a draft may hold. Anything else (patient lists, AI output,
// recordings, resolved recipients) is dropped.
export const DRAFT_FIELDS = [
  "engagementType", "engagementAt", "duration", "patientId", "patientLabel",
  "contactMethod", "locationType", "successful", "summary", "hrsnDomains",
  "resources", "cmActivity", "escalationOpen", "escRoles", "escUrgency", "escReason",
  // Client-generated row ids (no PHI): a restored draft reuses them, so a
  // save that already reached the server is not inserted a second time.
  "clientIds",
];

// ---- visit state --------------------------------------------------------------
export function expectedEnd(visit) {
  if (!visit) return null;
  if (visit.expected_end_at) return new Date(visit.expected_end_at);
  const start = new Date(visit.scheduled_at);
  if (isNaN(start.getTime())) return null;
  const inAt = visit.checked_in_at ? new Date(visit.checked_in_at) : null;
  const base = inAt && inAt > start ? inAt : start;
  const mins = Math.max(Number(visit.duration_minutes || visit.scheduled_duration_minutes) || 60, 5);
  return new Date(base.getTime() + mins * 60000);
}

// "upcoming" | "late" (start passed, not checked in) | "on_site" |
// "overdue_checkout" (past expected end, still on site) | "done"
export function fieldVisitState(visit, now = Date.now()) {
  if (!visit) return "upcoming";
  if (visit.checked_out_at) return "done";
  if (visit.checked_in_at) {
    const end = expectedEnd(visit);
    return end && now > end.getTime() ? "overdue_checkout" : "on_site";
  }
  const start = new Date(visit.scheduled_at).getTime();
  if (Number.isFinite(start) && now - start > 10 * 60000) return "late";
  return "upcoming";
}

export function minutesLabel(mins) {
  const m = Math.max(0, Math.round(Math.abs(mins)));
  if (m < 60) return m + " min";
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? h + "h " + r + "m" : h + "h";
}

// Next stop: first visit not checked in and not done, in time order.
export function nextStopId(visits) {
  const list = Array.isArray(visits) ? visits : [];
  const next = list.find((v) => !v.checked_in_at && !v.checked_out_at);
  return next ? next.visit_id : null;
}

// ---- errors -----------------------------------------------------------------------
const FIELD_ERRORS = {
  not_authorized: "That visit is not on your schedule.",
  visit_not_found: "That visit could not be found. Pull to refresh.",
  visit_not_open: "That visit is closed, so it cannot be checked in.",
  not_checked_in: "Check in first, then check out.",
  escalation_closed: "This escalation is already closed.",
  body_length: "Write a message (up to 2,000 characters).",
};

export function isNetworkError(e, online = (typeof navigator !== "undefined" ? navigator.onLine : true)) {
  if (online === false) return true;
  const m = String((e && (e.message || e.details)) || e || "");
  return /Failed to fetch|NetworkError|Load failed|network request failed|ERR_INTERNET_DISCONNECTED|fetch failed/i.test(m);
}

// Postgres unique violation: the row is already there (an earlier attempt
// that reached the server before the connection dropped).
export function isDuplicateKey(e) {
  if (!e) return false;
  return e.code === "23505" || /duplicate key value/i.test(String(e.message || ""));
}

export function fieldErrorMessage(e) {
  if (isNetworkError(e)) return "You are offline. It will retry when you have signal.";
  const m = String((e && e.message) || e || "");
  for (const code of Object.keys(FIELD_ERRORS)) {
    if (m.includes(code)) return FIELD_ERRORS[code];
  }
  return m || "Something went wrong.";
}

// ---- drafts --------------------------------------------------------------------
function safeStorage(storage) {
  if (storage) return storage;
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch (e) { return null; }
}

// One draft per user and visit; without a visit, per member; else "new".
export function draftKey(userId, { visitId, patientId } = {}) {
  if (!userId) return null;
  const scope = visitId ? "visit." + visitId : patientId ? "member." + patientId : "new";
  return DRAFT_PREFIX + userId + "." + scope;
}

export function pickDraftFields(form) {
  const out = {};
  if (!form) return out;
  for (const k of DRAFT_FIELDS) {
    if (form[k] !== undefined) out[k] = form[k];
  }
  return out;
}

// A draft worth keeping has a type or some text typed.
export function draftHasContent(fields) {
  if (!fields) return false;
  return !!(fields.engagementType || (fields.summary && String(fields.summary).trim())
    || (fields.escReason && String(fields.escReason).trim()) || (fields.patientLabel && String(fields.patientLabel).trim()));
}

export function saveDraft(key, form, { storage, now = Date.now(), pendingSave = false } = {}) {
  const st = safeStorage(storage);
  if (!st || !key) return false;
  const fields = pickDraftFields(form);
  try {
    if (!draftHasContent(fields)) { st.removeItem(key); return false; }
    // storage-key: pos.chwDraft.v1.
    st.setItem(key, JSON.stringify({ v: 1, savedAt: now, pendingSave: !!pendingSave, fields }));
    return true;
  } catch (e) {
    return false;
  }
}

export function loadDraft(key, { storage, now = Date.now() } = {}) {
  const st = safeStorage(storage);
  if (!st || !key) return null;
  try {
    const raw = st.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || d.v !== 1 || typeof d.savedAt !== "number" || now - d.savedAt > DRAFT_TTL_MS) {
      st.removeItem(key);
      return null;
    }
    return { savedAt: d.savedAt, pendingSave: !!d.pendingSave, fields: pickDraftFields(d.fields) };
  } catch (e) {
    try { st.removeItem(key); } catch (e2) { /* ignore */ }
    return null;
  }
}

export function clearDraft(key, { storage } = {}) {
  const st = safeStorage(storage);
  if (!st || !key) return;
  try { st.removeItem(key); } catch (e) { /* ignore */ }
}

// Every field draft on this device (all users). Called on sign-out.
export function clearAllFieldDrafts({ storage } = {}) {
  const st = safeStorage(storage);
  if (!st) return 0;
  let n = 0;
  try {
    const keys = [];
    for (let i = 0; i < st.length; i++) {
      const k = st.key(i);
      if (k && k.startsWith(DRAFT_PREFIX)) keys.push(k);
    }
    for (const k of keys) { st.removeItem(k); n++; }
  } catch (e) { /* ignore */ }
  return n;
}

// Remove expired (or unreadable) drafts for every user on this device.
// Called once when the app starts.
export function sweepExpiredDrafts({ storage, now = Date.now() } = {}) {
  const st = safeStorage(storage);
  if (!st) return 0;
  let n = 0;
  try {
    const keys = [];
    for (let i = 0; i < st.length; i++) {
      const k = st.key(i);
      if (k && k.startsWith(DRAFT_PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      if (!loadDraft(k, { storage: st, now })) n++;
    }
  } catch (e) { /* ignore */ }
  return n;
}

// Drafts for one user that still hold content (for the "unsent drafts" chip).
export function listDrafts(userId, { storage, now = Date.now() } = {}) {
  const st = safeStorage(storage);
  if (!st || !userId) return [];
  const prefix = DRAFT_PREFIX + userId + ".";
  const out = [];
  try {
    const keys = [];
    for (let i = 0; i < st.length; i++) {
      const k = st.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    for (const k of keys) {
      const d = loadDraft(k, { storage: st, now });
      if (d) out.push({ key: k, scope: k.slice(prefix.length), ...d });
    }
  } catch (e) { /* ignore */ }
  return out.sort((a, b) => b.savedAt - a.savedAt);
}

export function draftAgeLabel(savedAt, now = Date.now()) {
  const mins = Math.round((now - savedAt) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return mins + " min ago";
  const h = Math.round(mins / 60);
  if (h < 24) return h + "h ago";
  return Math.round(h / 24) + "d ago";
}

// ---- unread counts ----------------------------------------------------------------
// summary: escalation_unread_summary() -> { total, items: [{ escalation_id, unread }] }
export function unreadFor(summary, escalationId) {
  if (!summary || !Array.isArray(summary.items) || !escalationId) return 0;
  const hit = summary.items.find((i) => i.escalation_id === escalationId);
  return hit ? Number(hit.unread) || 0 : 0;
}

export function unreadTotal(summary) {
  if (!summary) return 0;
  if (Number.isFinite(Number(summary.total))) return Number(summary.total);
  return (summary.items || []).reduce((s, i) => s + (Number(i.unread) || 0), 0);
}
